import { Plus, Trash2 } from "lucide-react";
import {
  ALL_DAYS,
  DAY_KEYS,
  DAY_LABELS,
  DAY_LABELS_SHORT,
  type DayKey,
} from "@/lib/days";
import type {
  DailyRecurringTask,
  RecurringTask,
  RecurringTasksConfig,
  WeeklyRecurringTask,
  WeeklyRecurringTasksConfig,
} from "@/lib/schema";
import type { WidgetSettingsEditorProps } from "@/widgets/types";
import {
  checkableFromOf,
  dailyBreakdown,
  earliestCheckableFrom,
  latestCheckableFrom,
  maxScoreWeek,
  tasksForDay,
} from "./scoring";

type Props = WidgetSettingsEditorProps<RecurringTasksConfig>;

type TaskPatch = Partial<DailyRecurringTask & WeeklyRecurringTask>;

function lowerDay(dayKey: DayKey): string {
  return DAY_LABELS[dayKey].toLowerCase();
}

export function SettingsEditor({
  config,
  onChange,
  errors = {},
  scoringField,
  mode,
}: Props) {
  const update = (patch: Partial<RecurringTasksConfig>) => onChange(patch);
  const fieldError = (key: string) =>
    errors[key] ? (
      <p className="field-error" data-testid={`widget-error-${key}`}>
        {errors[key]}
      </p>
    ) : null;

  const isWeekly = config.reset === "weekly";

  const setTasks = (tasks: RecurringTask[]) =>
    update({ tasks } as Partial<RecurringTasksConfig>);

  const updateTask = (id: string, patch: TaskPatch) => {
    setTasks(config.tasks.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };

  // Flipping the mode rewrites every task, and the two shapes do not convert
  // losslessly — daily→weekly keeps only the earliest day, weekly→daily has
  // no set to restore. That is why the radio locks below once an existing
  // instance has tasks.
  const flipReset = (next: RecurringTasksConfig["reset"]) => {
    if (next === config.reset) return;
    if (next === "weekly") {
      const tasks: WeeklyRecurringTask[] = config.tasks.map((t) => ({
        id: t.id,
        label: t.label,
        checkableFrom: checkableFromOf(t),
      }));
      update({ reset: "weekly", tasks } as Partial<RecurringTasksConfig>);
      return;
    }
    const tasks: DailyRecurringTask[] = config.tasks.map((t) => ({
      id: t.id,
      label: t.label,
      visibleDays: [...ALL_DAYS],
    }));
    update({ reset: "daily", tasks } as Partial<RecurringTasksConfig>);
  };

  const addTask = () => {
    const newTask: RecurringTask = isWeekly
      ? { id: crypto.randomUUID(), label: "", checkableFrom: "mon" }
      : { id: crypto.randomUUID(), label: "", visibleDays: [...ALL_DAYS] };
    setTasks([...config.tasks, newTask]);
  };

  const removeTask = (id: string) => {
    setTasks(config.tasks.filter((t) => t.id !== id));
  };

  // Free to choose while creating; locked afterwards so a mis-click cannot
  // destroy a configured task list. Clearing the tasks unlocks it, which
  // keeps the instance's id and every weekly-point-summary row pointing at it.
  const resetLocked = mode === "edit" && config.tasks.length > 0;

  return (
    <div
      className="widget-settings"
      data-testid={`widget-settings-${config.id}`}
    >
      <div className="setting-row">
        <label className="setting-label" htmlFor={`label-${config.id}`}>
          Tittel
        </label>
        <div className="setting-control">
          <input
            id={`label-${config.id}`}
            className="input"
            type="text"
            value={config.label}
            onChange={(e) => update({ label: e.target.value })}
            data-testid="widget-label-input"
          />
          {fieldError("label")}
        </div>
      </div>

      <div className="setting-row setting-row--align-top">
        <span className="setting-label">Tilbakestilling</span>
        <div className="radio-group">
          <label>
            <input
              type="radio"
              name={`reset-${config.id}`}
              value="daily"
              checked={config.reset === "daily"}
              disabled={resetLocked}
              onChange={() => flipReset("daily")}
              data-testid="reset-daily-radio"
            />
            <span>Daglig</span>
            <small>Ny sjekkliste hver dag.</small>
          </label>
          <label>
            <input
              type="radio"
              name={`reset-${config.id}`}
              value="weekly"
              checked={config.reset === "weekly"}
              disabled={resetLocked}
              onChange={() => flipReset("weekly")}
              data-testid="reset-weekly-radio"
            />
            <span>Ukentlig</span>
            <small>Én avkrysning per uke. Poeng når alt er huket av.</small>
          </label>
          {resetLocked ? (
            <p className="hint" data-testid="reset-locked-hint">
              Tøm oppgavelisten for å bytte.
            </p>
          ) : null}
        </div>
      </div>

      {scoringField}

      <div className="setting-row">
        <label className="setting-label" htmlFor={`points-${config.id}`}>
          Poeng
          <small>Poeng for å fullføre hele settet.</small>
        </label>
        <div className="setting-control">
          <div className="num-input">
            <input
              id={`points-${config.id}`}
              type="number"
              min={0}
              value={config.points}
              onChange={(e) =>
                update({ points: Math.max(0, Number(e.target.value) || 0) })
              }
              data-testid="widget-points-input"
            />
            <span className="unit">p</span>
          </div>
          {fieldError("points")}
        </div>
      </div>

      <div className="task-list">
        <div className="section-head">
          <h3>Oppgaver</h3>
          <span className="hint">
            {isWeekly
              ? "Velg når hver oppgave kan krysses av."
              : "Velg hvilke dager hver oppgave vises."}
          </span>
        </div>
        {fieldError("tasks")}
        {fieldError("taskLabels")}
        {config.tasks.map((task) => (
          <div
            key={task.id}
            className="task-row"
            data-testid={`task-row-${task.id}`}
          >
            <input
              type="text"
              className="input"
              placeholder="Oppgave"
              value={task.label}
              onChange={(e) => updateTask(task.id, { label: e.target.value })}
              data-testid={`task-label-input-${task.id}`}
            />
            <div className="day-picker">
              {DAY_KEYS.map((k) => {
                const active = isWeekly
                  ? checkableFromOf(task) === k
                  : "visibleDays" in task && task.visibleDays.includes(k);
                return (
                  <button
                    type="button"
                    key={k}
                    className={`day-pill ${active ? "active" : ""}`}
                    onClick={() => {
                      if (isWeekly) {
                        updateTask(task.id, { checkableFrom: k });
                        return;
                      }
                      const days =
                        "visibleDays" in task ? task.visibleDays : [];
                      updateTask(task.id, {
                        visibleDays: active
                          ? days.filter((d) => d !== k)
                          : [...days, k],
                      });
                    }}
                    data-testid={`task-day-${task.id}-${k}`}
                  >
                    {DAY_LABELS_SHORT[k]}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="icon-btn-delete"
              onClick={() => removeTask(task.id)}
              aria-label="Slett oppgave"
              data-testid={`task-delete-${task.id}`}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn ghost icon-btn"
          onClick={addTask}
          data-testid="add-task-button"
        >
          <Plus size={14} />
          Legg til oppgave
        </button>
      </div>

      <Summary config={config} />
    </div>
  );
}

function WeeklySummary({ config }: { config: WeeklyRecurringTasksConfig }) {
  // The set needs every task, so it cannot finish before the last one opens.
  const earliestFinish = latestCheckableFrom(config);
  const earliestOpen = earliestCheckableFrom(config);
  const hidden =
    earliestOpen === null
      ? []
      : DAY_KEYS.slice(0, DAY_KEYS.indexOf(earliestOpen));
  return (
    <ul>
      <li data-testid="summary-weekly">
        Uken: alle oppgaver = {config.points}p
      </li>
      <li data-testid="summary-earliest-finish">
        Tidligst fullført:{" "}
        {earliestFinish === null ? "—" : lowerDay(earliestFinish)}
      </li>
      <li
        data-testid="summary-hidden-days"
        className={hidden.length === 0 ? undefined : "empty"}
      >
        Skjult:{" "}
        {hidden.length === 0 ? "ingen dager" : hidden.map(lowerDay).join(", ")}
      </li>
    </ul>
  );
}

function Summary({ config }: { config: RecurringTasksConfig }) {
  const max = maxScoreWeek(config);
  const goal = config.weekGoal;
  const goalSet = Number.isFinite(goal);
  const goalPct = !goalSet || max === 0 ? null : Math.round((goal / max) * 100);
  return (
    <div className="summary" data-testid={`widget-summary-${config.id}`}>
      <h4>Sammendrag</h4>
      <p>
        Maks poeng denne uka: <strong data-testid="summary-max">{max}p</strong>
      </p>
      <p>
        Mål:{" "}
        <strong data-testid="summary-goal">
          {!goalSet
            ? "—"
            : goalPct === null
              ? `${goal}p`
              : `${goal}p (${goalPct}%)`}
        </strong>
      </p>
      {config.reset === "daily" ? (
        <ul>
          {dailyBreakdown(config).map((b) => {
            const tasks = tasksForDay(config, b.dayKey);
            if (!b.hasSet) {
              return (
                <li
                  key={b.dayKey}
                  data-testid={`summary-day-${b.dayKey}`}
                  className="empty"
                >
                  {DAY_LABELS[b.dayKey]}: ingen oppgaver synlige · 0p
                </li>
              );
            }
            return (
              <li key={b.dayKey} data-testid={`summary-day-${b.dayKey}`}>
                {DAY_LABELS[b.dayKey]}:{" "}
                {tasks.map((t) => t.label || "(uten navn)").join(" + ")} ={" "}
                {config.points}p
              </li>
            );
          })}
        </ul>
      ) : (
        <WeeklySummary config={config} />
      )}
    </div>
  );
}
